import { inject, Injectable } from '@angular/core';
import { AppNotificationSeverity } from './app-notification-severity';
import { AppNotificationGateway } from './app-notification.gateway';
import { AppTranslationService } from '@service/translation/app-translation.service';
import { AppErrorItem } from '@domain-utils/app-error-item';

/**
 * Servicio de notificaciones
 */
@Injectable({
  providedIn: 'root'
})
export class AppNotificationService {
  /**
   * Servicio de traducción
   */
  private translateService = inject(AppTranslationService);

  /**
   * Constructor
   * @param notificationGateway gateway de notificaciones
   */
  constructor(private readonly notificationGateway: AppNotificationGateway) {}

  /**
   * Método para mostrar una notificación de éxito
   * @param message  mensaje de la notificación
   * @param params parámetros para la interpolación del mensaje
   */
  success(message: string, params?: Record<string, string | number>): void {
    console.log('AppNotificationService - success message:', message);
    this.translateService
      .getMany([
        { key: 'notification.successTitle' },
        { key: message, params: params as Record<string, string> }
      ])
      .subscribe((translations) => {
        const title = translations.find(
          (t) => t.key === 'notification.successTitle'
        )!.value;
        const translatedMessage = translations.find(
          (t) => t.key === message
        )!.value;

        this.notificationGateway.notify({
          title: title,
          message: translatedMessage,
          severity: AppNotificationSeverity.SUCCESS
        });
      });
  }

  /**
   * Método para mostrar una notificación de error
   * @param message mensaje de la notificación
   */
  /**
   * Método para mostrar una notificación de error
   * @param message clave del mensaje de la notificación
   * @param params parámetros para la interpolación del mensaje (ej: {diffDays: 10, maxDays: 5})
   */
  error(message: string, params?: Record<string, string | number>): void {
    this.translateService
      .getMany([
        { key: 'notification.errorTitle' },
        { key: message, params: params as Record<string, string> }
      ])
      .subscribe((translations) => {
        const title = translations.find(
          (t) => t.key === 'notification.errorTitle'
        )!.value;
        const translatedMessage = translations.find(
          (t) => t.key === message
        )!.value;

        this.notificationGateway.notify({
          title: title,
          message: translatedMessage,
          severity: AppNotificationSeverity.ERROR
        });
      });
  }

  /**
   * Método para mostrar una notificación de error con varios mensajes
   * @param messages mensajes de la notificación
   */
  errorMany(messages: AppErrorItem[]): void {
    messages.forEach((message) => {
      this.error(message.message);
    });
  }

  /**
   * Método para mostrar una notificación de advertencia
   * @param message mensaje de la notificación
   * @param params parámetros para la interpolación del mensaje
   */
  warn(message: string, params?: Record<string, string | number>): void {
    this.translateService
      .getMany([
        { key: 'notification.warningTitle' },
        { key: message, params: params as Record<string, string> }
      ])
      .subscribe((translations) => {
        const title = translations.find(
          (t) => t.key === 'notification.warningTitle'
        )!.value;
        const translatedMessage = translations.find(
          (t) => t.key === message
        )!.value;

        this.notificationGateway.notify({
          title: title,
          message: translatedMessage,
          severity: AppNotificationSeverity.WARNING
        });
      });
  }

  /**
   * Método para mostrar una notificación de información
   * @param message mensaje de la notificación
   * @param params parámetros para la interpolación del mensaje
   */
  info(message: string, params?: Record<string, string | number>): void {
    this.translateService
      .getMany([
        { key: 'notification.infoTitle' },
        { key: message, params: params as Record<string, string> }
      ])
      .subscribe((translations) => {
        const title = translations.find(
          (t) => t.key === 'notification.infoTitle'
        )!.value;
        const translatedMessage = translations.find(
          (t) => t.key === message
        )!.value;

        this.notificationGateway.notify({
          title: title,
          message: translatedMessage,
          severity: AppNotificationSeverity.INFO
        });
      });
  }
}
